import { tap, catchError, type Observable } from 'rxjs';

import { Router } from '@angular/router';
import { inject, Service } from '@angular/core';

import { AuthStore } from './auth-store';
import { TokenStore } from '@core/services';

import { ToastService } from '@shared/components/toast';

import { AuthApi } from './auth-api';
import { type AuthToken } from './models/token';
import { type LoginPayload } from './models/login';
import { type ResetPassword } from './models/reset-password';
import { resetPasswordToDto } from './mappers/reset-password';

@Service()
export class AuthFacade {
  private readonly router = inject(Router);
  private readonly authApi = inject(AuthApi);
  private readonly authStore = inject(AuthStore);
  private readonly toastService = inject(ToastService);
  private readonly tokenStore = inject(TokenStore);

  navigateAfterLogin(): Promise<boolean> {
    return this.router.navigate(['/users']);
  }

  login(data: LoginPayload): Observable<AuthToken> {
    return this.authApi.login(data).pipe(
      tap((accessToken) => this.tokenStore.setAccessToken(accessToken)),
      catchError(this.toastService.httpError),
    );
  }

  logout(): void {
    this.authApi.logout().subscribe({
      error: () => {},
    });
    this.clearSession();
    this.router.navigateByUrl('/auth/login');
  }

  forgotPassword(email: string): Observable<AuthToken> {
    return this.authApi.forgotPassword(email).pipe(
      tap((resetToken) => this.tokenStore.setResetToken(resetToken)),
      catchError(this.toastService.httpError),
    );
  }

  resetPassword(data: ResetPassword): Observable<null> {
    return this.authApi.resetPassword(resetPasswordToDto(data));
  }

  refreshToken(): Observable<AuthToken> {
    return this.authApi.refreshToken().pipe(tap((accessToken) => this.tokenStore.setAccessToken(accessToken)));
  }

  private clearSession(): void {
    this.tokenStore.clearAccessToken();
    this.authStore.setStatusGuest();
  }
}
